import { invoke } from '@tauri-apps/api/core';

import { sleep } from '@/lib/utils';
import {
  GENIE_COMMAND_PREFIX as COMMAND_PREFIX,
  extractPluginError,
  formatError,
} from './constants';

export class GenieApiCommandError extends Error {
  constructor(
    message: string,
    public readonly errorCode?: string
  ) {
    super(message);
    this.name = 'GenieApiCommandError';
  }
}

export const isGenieNotConnectedError = (error: unknown): boolean =>
  error instanceof GenieApiCommandError &&
  error.errorCode === 'INTEGRATION_GENIE_NOT_CONNECTED';

/**
 * Genie Integration API
 *
 * This module provides TypeScript wrappers around the Tauri commands
 * for interacting with the Genie medical practice management system.
 */

/**
 * Gets the Genie instance URL from the local configuration file EnginedServer.4DLink
 *
 * @returns The instance URL
 * @throws Error if the configuration file cannot be read or parsed
 */
export async function genieGetInstanceUrl(): Promise<string> {
  try {
    const instanceUrl = await Promise.race([
      invoke<string>(`${COMMAND_PREFIX}get_instance_url`),
      sleep(10000).then(() =>
        Promise.reject(new Error('Genie instance URL retrieval timed out'))
      ),
    ]);
    return instanceUrl;
  } catch (error) {
    throw new Error(`Failed to get Genie instance URL: ${formatError(error)}`);
  }
}

export interface GenieConnectionConfig {
  instanceUrl: string;
  sender: string;
  password: string;
}

/**
 * Connects to the Genie server and establishes a session.
 *
 * @param config - Connection configuration
 * @returns The connection ID from Genie
 * @throws Error if connection fails
 */
export async function genieConnect(config: GenieConnectionConfig): Promise<string> {
  try {
    const connectionId = await invoke<string>(`${COMMAND_PREFIX}connect`, {
      instanceUrl: config.instanceUrl,
      sender: config.sender,
      password: config.password,
    });
    return connectionId;
  } catch (error) {
    throw new Error(`Failed to connect to Genie: ${formatError(error)}`);
  }
}

/**
 * Gets the currently installed Genie version.
 *
 * @returns The Genie version number
 * @throws Error if not connected or if the request fails
 */
export async function genieGetVersion(): Promise<number> {
  try {
    const version = await invoke<number>(`${COMMAND_PREFIX}get_version`);
    return version;
  } catch (error) {
    throw new Error(`Failed to get Genie version: ${formatError(error)}`);
  }
}

/**
 * Closes the connection to the Genie server.
 *
 * @throws Error if not connected or if closing fails
 */
export async function genieClose(): Promise<void> {
  try {
    await invoke<void>(`${COMMAND_PREFIX}close`);
  } catch (error) {
    throw new Error(`Failed to close Genie connection: ${formatError(error)}`);
  }
}

/**
 * Practitioner information
 */
export interface PractitionerData {
  id: number;
  firstName: string;
  lastName: string;
}

/**
 * Gets the list of available practitioners from Genie.
 *
 * @returns Array of practitioner information
 * @throws Error if not connected or if the request fails
 */
export async function genieGetPractitioners(): Promise<PractitionerData[]> {
  try {
    const practitioners = await invoke<PractitionerData[]>(
      `${COMMAND_PREFIX}get_practitioners`
    );
    return practitioners;
  } catch (error) {
    const { errorCode } = extractPluginError(error);
    throw new GenieApiCommandError(
      `Failed to get practitioners: ${formatError(error)}`,
      errorCode
    );
  }
}
